import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @type {boolean} */
  #stale = false;

  #handleCartAdd = () => {
    // Cart changed on this page — drawer section usually updates via cart:update.
    // Still mark stale so a later open always verifies against /cart.js.
    this.#stale = true;
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
  };

  /**
   * Any pageshow can leave stale drawer HTML (bfcache / soft back).
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    this.#stale = true;
    if (event.persisted) {
      // Warm the section in the background after back/forward restore
      this.#refreshDrawerSection();
    }
  };

  #getSectionId() {
    const cartItems = this.querySelector('cart-items-component');
    return cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : null;
  }

  #refreshDrawerSection = async () => {
    const sectionId = this.#getSectionId();
    if (!sectionId) return false;
    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
      this.#stale = false;
      return true;
    } catch (_) {
      return false;
    }
  };

  /**
   * Always verify drawer markup against the live cart before opening.
   * Full page refresh works because Liquid re-renders; this does the same via Section Rendering API.
   */
  #ensureDrawerContentsAreFresh = async () => {
    const sectionId = this.#getSectionId();
    if (!sectionId) return;

    let liveCount = 0;
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (response.ok) {
        const cart = await response.json();
        liveCount = Number(cart.item_count) || 0;
      }
    } catch (_) {
      if (this.#stale) await this.#refreshDrawerSection();
      return;
    }

    const dialog = this.querySelector('dialog');
    const rowCount = this.querySelectorAll('.cart-items__table-row').length;
    const looksEmpty =
      Boolean(dialog?.classList.contains('cart-drawer--empty')) || rowCount === 0;

    // Mismatch between server cart and drawer DOM → re-render (the refresh bug)
    if (this.#stale || (liveCount > 0 && looksEmpty) || (liveCount === 0 && !looksEmpty)) {
      await this.#refreshDrawerSection();
    }
  };

  open() {
    this.#ensureDrawerContentsAreFresh().finally(() => {
      this.showDialog();

      customElements.whenDefined('shopify-payment-terms').then(() => {
        const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
        const cta = installmentsContent?.querySelector('#shopify-installments-cta');
        cta?.addEventListener('click', this.closeDialog, { once: true });
      });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
